import { useState, useRef, useEffect } from "react";
import "../styles/OnboardingForm.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const STEP_ICONS = [
  "/assets/icons/personal.png",
  "/assets/icons/address.png",
  "/assets/icons/education.png",
  "/assets/icons/identity.png",
  "/assets/icons/bank.png",
  "/assets/icons/documents.png",
];

const STEPS = [
  { id: 1, label: "Personal Info" },
  { id: 2, label: "Address" },
  { id: 3, label: "Education" },
  { id: 4, label: "Identity" },
  { id: 5, label: "Bank Details" },
  { id: 6, label: "Documents" },
];

const STEP_TITLES = [
  "Personal Information",
  "Address Details",
  "Education Qualification",
  "Identity Details",
  "Bank Details",
  "Document Upload",
];

// ─── Input ────────────────────────────────────────────────────
function Input({ label, required, placeholder, type = "text", value, onChange, error, allowOnly }) {
  const handleChange = (e) => {
    let val = e.target.value;
    if (allowOnly === "alpha")        val = val.replace(/[^A-Za-z\s]/g, "");
    if (allowOnly === "numeric")      val = val.replace(/[^0-9]/g, "");
    if (allowOnly === "alphanumeric") val = val.replace(/[^A-Za-z0-9]/g, "");
    if (allowOnly === "pan")          val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (allowOnly === "ifsc")         val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (allowOnly === "alphaspace")   val = val.replace(/[^A-Za-z0-9\s]/g, "");
    if (allowOnly === "institution")  val = val.replace(/[^A-Za-z0-9\s.,\-&]/g, "");
    if (allowOnly === "university")   val = val.replace(/[^A-Za-z0-9\s.,\-&]/g, "");
    onChange({ target: { value: val } });
  };
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="required-star">*</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className="form-input"
      />
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────
function Select({ label, required, options, placeholder, value, onChange, error }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="required-star">*</span>}
      </label>
      <div className="custom-select-wrapper" ref={ref}>
        <div
          className={`custom-select-trigger ${isOpen ? "open" : ""} ${!value ? "placeholder" : ""}`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span>{value || placeholder || `Select ${label}`}</span>
          <svg className={`select-chevron ${isOpen ? "rotated" : ""}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 5l4 4 4-4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {isOpen && (
          <div className="custom-select-dropdown">
            {options.map((o) => (
              <div key={o} className={`custom-select-option ${value === o ? "selected" : ""}`}
                onClick={() => { onChange({ target: { value: o } }); setIsOpen(false); }}>
                {o}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ─── DatePickerInput ──────────────────────────────────────────
function DatePickerInput({ label, required, value, onChange, error }) {
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [inputValue, setInputValue] = useState(
    value
      ? `${String(new Date(value).getDate()).padStart(2, "0")} / ${String(new Date(value).getMonth() + 1).padStart(2, "0")} / ${new Date(value).getFullYear()}`
      : ""
  );
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handlePickerChange = (date) => {
    setSelectedDate(date);
    setIsOpen(false);
    const formatted = date
      ? `${String(date.getDate()).padStart(2, "0")} / ${String(date.getMonth() + 1).padStart(2, "0")} / ${date.getFullYear()}`
      : "";
    setInputValue(formatted);
    onChange({ target: { value: formatted } });
  };
  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d/\s]/g, "");
    setInputValue(val);
    const clean = val.replace(/\s/g, "");
    const parts = clean.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10), month = parseInt(parts[1], 10) - 1, year = parseInt(parts[2], 10);
      if (year > 999 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
        const parsed = new Date(year, month, day);
        if (!isNaN(parsed)) { setSelectedDate(parsed); onChange({ target: { value: val } }); return; }
      }
    }
    onChange({ target: { value: val } });
  };
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="required-star">*</span>}
      </label>
      <div className="date-wrapper" ref={wrapperRef}>
        <input type="text" placeholder="DD / MM / YYYY" className="custom-date-input"
          value={inputValue} onChange={handleInputChange} maxLength={14} />
        <button type="button" className="calendar-toggle-btn" onClick={() => setIsOpen((prev) => !prev)}>
          <img src="/assets/icons/calendar.png" alt="calendar" className="calendar-icon-img" />
        </button>
        {isOpen && (
          <div className="calendar-dropdown">
            <DatePicker selected={selectedDate} onChange={handlePickerChange} inline
              showYearDropdown scrollableYearDropdown yearDropdownItemNumber={100} />
          </div>
        )}
      </div>
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ─── Textarea ─────────────────────────────────────────────────
function Textarea({ label, required, placeholder, value, onChange, error }) {
  return (
    <div className="form-field">
      <label className="form-label">
        {label}
        {required && <span className="required-star">*</span>}
      </label>
      <textarea placeholder={placeholder} value={value} onChange={onChange} rows={4} className="form-textarea" />
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ─── FileUpload ───────────────────────────────────────────────
// IMPORTANT: now stores the actual File object, not just the name
function FileUpload({ label, required, onChange, fileObj, error, accept = ".pdf" })  {
  const ref = useRef();
  const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const isImage = accept === ".jpg,.jpeg";
  const minSize = isImage ? 100 * 1024 : 20 * 1024;  // 100KB for image, 20KB for PDF
  const minLabel = isImage ? "100KB" : "20KB";

  if (file.size < minSize) {
    alert(`"${file.name}" is too small. Minimum file size is ${minLabel}.`);
    ref.current.value = "";
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    alert(`"${file.name}" is too large. Maximum file size is 5MB.`);
    ref.current.value = "";
    return;
  }
  onChange(file);
};
  const fileName = fileObj ? fileObj.name : null;
  return (
    <div className="file-upload-field">
      <label className="form-label">
        {label}
        {required && <span className="required-star">*</span>}
      </label>
      <div
        className={`file-uploaded-box ${fileName ? "file-selected" : ""}`}
        onClick={() => ref.current.click()}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" />
          <polyline points="17 8 12 3 7 8" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="12" y1="3" x2="12" y2="15" stroke={fileName ? "#22c55e" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" />
        </svg>
        {fileName ? <span className="file-name">{fileName}</span> : <span className="file-placeholder">Choose File</span>}
        {fileName && <span className="file-check">✓</span>}
      </div>
      <input ref={ref} type="file" className="file-input-hidden" onChange={handleFileChange} accept={accept} />
      {error && <span className="field-error">{error}</span>}
      <span className="file-size-hint">
  {accept === ".jpg,.jpeg" ? "JPEG only · 100KB – 5MB" : "PDF only · 20KB – 5MB"}
</span>
    </div>
  );
}

// ─── Steps 1–5 (unchanged logic, just wired to new data shape) ─
function Step1({ data, set, errors }) {
  const f = (k) => ({ value: data[k] || "", onChange: (e) => set({ ...data, [k]: e.target.value }), error: errors[k] || "" });
  return (
    <>
      <div className="form-grid-2">
        <Input label="Full Name" required placeholder="Enter your full name" allowOnly="alpha" {...f("fullName")} />
        <DatePickerInput label="Date of Birth" required {...f("dob")} />
      </div>
      <div className="form-grid-2">
        <Select label="Gender" placeholder="Select Gender" options={["Male", "Female", "Other"]} {...f("gender")} />
        <Select label="Marital Status" placeholder="Select Status" options={["Single", "Married", "Divorced", "Widowed"]} {...f("marital")} />
      </div>
      <div className="form-grid-2">
        <Input label="Nationality" placeholder="Enter Nationality" allowOnly="alpha" {...f("nationality")} />
        <Input label="Personal Email ID" required type="email" placeholder="Enter Email ID" {...f("email")} />
      </div>
      <div className="form-grid-2">
        <Input label="Mobile Number" required placeholder="Enter Mobile Number" allowOnly="numeric" {...f("mobile")} />
        <Input label="Alternate Number" placeholder="Enter Alternate Number" allowOnly="numeric" {...f("altMobile")} />
      </div>
    </>
  );
}

function Step2({ data, set, errors }) {
  const f = (k) => ({ value: data[k] || "", onChange: (e) => set({ ...data, [k]: e.target.value }), error: errors[k] || "" });
  const states = [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
    "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
    "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
    "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
    "Uttarakhand","West Bengal","Delhi",
  ];
  return (
    <>
      <Textarea label="Address" required placeholder="Enter your address" {...f("address")} />
      <div className="form-grid-2">
        <Input label="City" required placeholder="Enter your City" allowOnly="alpha" {...f("city")} />
        <Select label="State" required placeholder="Select State" options={states} {...f("state")} />
      </div>
      <div className="half-width">
        <Input label="Pin Code" required placeholder="Enter Pin Code" allowOnly="numeric" {...f("pin")} />
      </div>
    </>
  );
}

function Step3({ data, set, errors }) {
  const f = (k) => ({ value: data[k] || "", onChange: (e) => set({ ...data, [k]: e.target.value }), error: errors[k] || "" });
  return (
    <>
      <div className="form-grid-2">
        <Select label="Qualification" required placeholder="Select Qualification"
          options={["High School","Diploma","Bachelor's","Master's","PhD","Other"]} {...f("qualification")} />
        <Input label="Institution Name" required placeholder="Enter Institution Name" allowOnly="institution" {...f("institution")} />
      </div>
      <div className="form-grid-2">
        <Input label="University/Board" placeholder="Enter University/Board" allowOnly="university" {...f("university")} />
        <Input label="Year of Passing" required placeholder="YYYY" allowOnly="numeric" {...f("yearOfPassing")} />
      </div>
      <div className="half-width">
        <Input label="Percentage/CGPA" placeholder="Enter Percentage/CGPA" {...f("percentage")} />
      </div>
    </>
  );
}

function Step4({ data, set, errors }) {
  const f = (k) => ({ value: data[k] || "", onChange: (e) => set({ ...data, [k]: e.target.value }), error: errors[k] || "" });
  return (
    <>
      <div className="form-grid-2">
        <Input label="PAN Number" required placeholder="Enter PAN Number" allowOnly="pan" {...f("pan")} />
        <Input label="Aadhaar Number" required placeholder="12-Digit Aadhaar Number" allowOnly="numeric" {...f("aadhaar")} />
      </div>
      <div className="form-grid-2">
        <Input label="Passport Number" placeholder="Enter Passport Number" allowOnly="alphanumeric" {...f("passport")} />
        <Input label="UAN" placeholder="Enter UAN" allowOnly="numeric" {...f("uan")} />
      </div>
      <div className="half-width">
        <Input label="ESI Number" placeholder="Enter ESI Number" allowOnly="numeric" {...f("esi")} />
      </div>
    </>
  );
}

function Step5({ data, set, errors }) {
  const f = (k) => ({ value: data[k] || "", onChange: (e) => set({ ...data, [k]: e.target.value }), error: errors[k] || "" });
  return (
    <>
      <div className="form-grid-2">
        <Input label="Bank Name" required placeholder="Enter Bank Name" allowOnly="alpha" {...f("bankName")} />
        <Input label="Account Holder Name" required placeholder="As Per Bank Records" allowOnly="alpha" {...f("accountHolder")} />
      </div>
      <div className="form-grid-2">
        <Input label="Account Number" required placeholder="Enter Account Number" allowOnly="numeric" {...f("accountNo")} />
        <Input label="IFSC Code" required placeholder="Enter IFSC Code" allowOnly="ifsc" {...f("ifsc")} />
      </div>
      <div className="half-width">
        <Input label="Branch" placeholder="Enter Branch Name" allowOnly="alphaspace" {...f("branch")} />
      </div>
    </>
  );
}

// ─── Step 6 ───────────────────────────────────────────────────
// data[key] is now a File object (or undefined); we display file.name
function Step6({ data, set, errors }) {
  const docs = [
    { key: "aadhaarDoc",   label: "Aadhaar Card",          required: true  },
    { key: "panDoc",       label: "PAN Card",               required: true  },
    { key: "tenth",        label: "10th Certificate",       required: true  },
    { key: "twelfth",      label: "12th Certificate",       required: true  },
    { key: "consolidated", label: "Consolidated Marksheet", required: true  },
    { key: "experience",   label: "Experience Letters",     required: false },
    { key: "relieving",    label: "Relieving Letter",       required: false },
    { key: "passport", label: "Passport size Photo", required: true, accept: ".jpg,.jpeg" },
  ];
  return (
    <>
      <div className="docs-grid">
        {docs.map((d) => (
         <FileUpload
  key={d.key}
  label={d.label}
  required={d.required}
  accept={d.accept}        // ← add this one prop
  fileObj={data[d.key] || null}
  onChange={(file) => set({ ...data, [d.key]: file })}
  error={errors[d.key] || ""}
/>
        ))}
      </div>
      <div className="declaration-row">
        <input
          type="checkbox"
          id="declaration"
          checked={data.declaration || false}
          onChange={(e) => set({ ...data, declaration: e.target.checked })}
        />
        <label htmlFor="declaration" className="declaration-label">
          I hereby declare that the information provided is true and correct to the best of my knowledge.
        </label>
      </div>
      {errors.declaration && <span className="field-error">{errors.declaration}</span>}
    </>
  );
}

// ─── Stepper ──────────────────────────────────────────────────
function Stepper({ current, setCurrent, submitted }) {
  return (
    <div className="stepper-wrapper">
      {STEPS.map((s, idx) => {
        const done   = submitted ? true : s.id < current;
        const active = submitted ? false : s.id === current;
        return (
          <div key={s.id}
            className={`step-item ${done ? "done" : ""} ${active ? "active" : ""} ${!submitted && s.id > current ? "disabled" : ""}`}
            onClick={() => !submitted && s.id <= current && setCurrent(s.id)}>
            <div className={`step-bubble ${active ? "active" : ""} ${done ? "done" : ""}`}>
              {done ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <img src={STEP_ICONS[idx]} alt={s.label} className="step-icon" />
              )}
            </div>
            <span className={`step-label ${active ? "active" : ""} ${done ? "done" : ""}`}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Validation (same as before) ─────────────────────────────
const REGEX = {
  fullName:      /^[A-Za-z\s]{2,100}$/,
  nationality:   /^[A-Za-z]+$/,
  email:         /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  city:          /^[A-Za-z\s]+$/,
  pin:           /^\d{6}$/,
  mobile:        /^\d{10}$/,
  altMobile:     /^\d{10}$/,
  institution:   /^[A-Za-z0-9\s.,\-&]+$/,
  university:    /^[A-Za-z0-9\s.,\-&]+$/,
  yearOfPassing: /^\d{4}$/,
  percentage:    /^\d+(\.\d+)?(%|\s*(CGPA)?)?$/i,
  pan:           /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  aadhaar:       /^\d{12}$/,
  passport:      /^[A-Za-z0-9]{6,9}$/,
  uan:           /^\d{12}$/,
  esi:           /^\d{10,17}$/,
  accountNo:     /^\d{9,18}$/,
  bankName:      /^[A-Za-z\s]+$/,
  accountHolder: /^[A-Za-z\s]+$/,
  ifsc:          /^[A-Z]{4}0[A-Z0-9]{6}$/,
  branch:        /^[A-Za-z0-9\s]+$/,
};
const ERROR_MSGS = {
  fullName:      "Full name must be 2–100 alphabetic characters and spaces only",
  nationality:   "Nationality must contain alphabets only",
  email:         "Enter a valid email address (e.g. name@email.com)",
  city:          "City must contain alphabets and spaces only",
  pin:           "Pin code must be exactly 6 digits",
  mobile:        "Mobile number must be exactly 10 digits",
  altMobile:     "Alternate number must be exactly 10 digits",
  institution:   "Institution name may contain alphabets, numbers, spaces, and . , - & only",
  university:    "University/Board may contain alphabets, numbers, spaces, and . , - & only",
  yearOfPassing: "Year of passing must be exactly 4 digits (e.g. 2024)",
  percentage:    "Enter a valid percentage or CGPA (e.g. 75, 75%, 8.2 CGPA)",
  pan:           "PAN must be in the format ABCDE1234F (5 letters, 4 digits, 1 letter)",
  aadhaar:       "Aadhaar number must be exactly 12 digits",
  passport:      "Passport number must be 6–9 alphanumeric characters",
  uan:           "UAN must be exactly 12 digits",
  esi:           "ESI number must be 10–17 digits",
  accountNo:     "Account number must be 9–18 digits",
  bankName:      "Bank name must contain alphabets and spaces only",
  accountHolder: "Account holder name must contain alphabets and spaces only",
  ifsc:          "IFSC code must be 11 characters (e.g. SBIN0001234)",
  branch:        "Branch name may contain alphabets, numbers, and spaces only",
};

function validateField(key, value) {
  if (!value || String(value).trim() === "") return "";
  const regex = REGEX[key];
  if (!regex) return "";
  return regex.test(String(value).trim()) ? "" : ERROR_MSGS[key];
}

// ─── Main Export ──────────────────────────────────────────────
export default function EmployeeOnboardingForm() {
  const [current,    setCurrent]    = useState(1);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);   // loading state
  const [apiError,   setApiError]   = useState("");      // server error msg
  const [formData,   setFormData]   = useState({});
  const [errors,     setErrors]     = useState({});

  const updateStep = (id, data) => setFormData((prev) => ({ ...prev, [id]: data }));
  const getStep    = (id)       => formData[id] || {};

  const validateStep = (step) => {
    const d = formData[step] || {};
    const newErrors = {};
    const format = (key) => {
      if (d[key] && String(d[key]).trim() !== "") {
        const msg = validateField(key, d[key]);
        if (msg) newErrors[key] = msg;
      }
    };

    if (step === 1) {
      if (!d.fullName) newErrors.fullName = "This field is required";
      if (!d.dob)      newErrors.dob      = "This field is required";
      if (!d.email)    newErrors.email    = "This field is required";
      if (!d.mobile)   newErrors.mobile   = "This field is required";
      if (d.fullName) { const m = validateField("fullName", d.fullName); if (m) newErrors.fullName = m; }
      if (d.email)    { const m = validateField("email", d.email);       if (m) newErrors.email    = m; }
      if (d.mobile)   { const m = validateField("mobile", d.mobile);     if (m) newErrors.mobile   = m; }
      format("nationality"); format("altMobile");
    }
    if (step === 2) {
      if (!d.address) newErrors.address = "This field is required";
      if (!d.city)    newErrors.city    = "This field is required";
      if (!d.state)   newErrors.state   = "This field is required";
      if (!d.pin)     newErrors.pin     = "This field is required";
      if (d.city) { const m = validateField("city", d.city); if (m) newErrors.city = m; }
      if (d.pin)  { const m = validateField("pin",  d.pin);  if (m) newErrors.pin  = m; }
    }
    if (step === 3) {
      if (!d.qualification) newErrors.qualification = "This field is required";
      if (!d.institution)   newErrors.institution   = "This field is required";
      if (!d.yearOfPassing) newErrors.yearOfPassing = "This field is required";
      if (d.institution)   { const m = validateField("institution",   d.institution);   if (m) newErrors.institution   = m; }
      if (d.yearOfPassing) { const m = validateField("yearOfPassing", d.yearOfPassing); if (m) newErrors.yearOfPassing = m; }
      format("university"); format("percentage");
    }
    if (step === 4) {
      if (!d.pan)     newErrors.pan     = "This field is required";
      if (!d.aadhaar) newErrors.aadhaar = "This field is required";
      if (d.pan)     { const m = validateField("pan",     d.pan);     if (m) newErrors.pan     = m; }
      if (d.aadhaar) { const m = validateField("aadhaar", d.aadhaar); if (m) newErrors.aadhaar = m; }
      format("passport"); format("uan"); format("esi");
    }
    if (step === 5) {
      if (!d.bankName)      newErrors.bankName      = "This field is required";
      if (!d.accountHolder) newErrors.accountHolder = "This field is required";
      if (!d.accountNo)     newErrors.accountNo     = "This field is required";
      if (!d.ifsc)          newErrors.ifsc          = "This field is required";
      if (d.bankName)      { const m = validateField("bankName",      d.bankName);      if (m) newErrors.bankName      = m; }
      if (d.accountHolder) { const m = validateField("accountHolder", d.accountHolder); if (m) newErrors.accountHolder = m; }
      if (d.accountNo)     { const m = validateField("accountNo",     d.accountNo);     if (m) newErrors.accountNo     = m; }
      if (d.ifsc)          { const m = validateField("ifsc",          d.ifsc);          if (m) newErrors.ifsc          = m; }
      format("branch");
    }
    if (step === 6) {
      if (!d.aadhaarDoc)   newErrors.aadhaarDoc   = "This field is required";
      if (!d.panDoc)       newErrors.panDoc       = "This field is required";
      if (!d.tenth)        newErrors.tenth        = "This field is required";
      if (!d.twelfth)      newErrors.twelfth      = "This field is required";
      if (!d.consolidated) newErrors.consolidated = "This field is required";
      if (!d.passport)     newErrors.passport     = "This field is required";
      if (!d.declaration)  newErrors.declaration  = "Please accept the declaration.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Build FormData and POST to backend ──────────────────────
  const submitToBackend = async () => {
    setSubmitting(true);
    setApiError("");

    const fd = new FormData();

    // Step 1
    const s1 = formData[1] || {};
    fd.append("fullName",    s1.fullName    || "");
    fd.append("dob",         s1.dob         || "");
    fd.append("gender",      s1.gender      || "");
    fd.append("marital",     s1.marital     || "");
    fd.append("nationality", s1.nationality || "");
    fd.append("email",       s1.email       || "");
    fd.append("mobile",      s1.mobile      || "");
    fd.append("altMobile",   s1.altMobile   || "");

    // Step 2
    const s2 = formData[2] || {};
    fd.append("address", s2.address || "");
    fd.append("city",    s2.city    || "");
    fd.append("state",   s2.state   || "");
    fd.append("pin",     s2.pin     || "");

    // Step 3
    const s3 = formData[3] || {};
    fd.append("qualification", s3.qualification || "");
    fd.append("institution",   s3.institution   || "");
    fd.append("university",    s3.university    || "");
    fd.append("yearOfPassing", s3.yearOfPassing || "");
    fd.append("percentage",    s3.percentage    || "");

    // Step 4
    const s4 = formData[4] || {};
    fd.append("pan",      s4.pan      || "");
    fd.append("aadhaar",  s4.aadhaar  || "");
    fd.append("passport", s4.passport || "");
    fd.append("uan",      s4.uan      || "");
    fd.append("esi",      s4.esi      || "");

    // Step 5
    const s5 = formData[5] || {};
    fd.append("bankName",      s5.bankName      || "");
    fd.append("accountHolder", s5.accountHolder || "");
    fd.append("accountNo",     s5.accountNo     || "");
    fd.append("ifsc",          s5.ifsc          || "");
    fd.append("branch",        s5.branch        || "");

    // Step 6 — file objects
    const s6 = formData[6] || {};
    const fileKeys = ["aadhaarDoc", "panDoc", "tenth", "twelfth", "consolidated", "experience", "relieving", "passport"];
    fileKeys.forEach((key) => {
      if (s6[key] instanceof File) fd.append(key, s6[key]);
    });
    fd.append("declaration", s6.declaration ? "true" : "false");

    try {
      const API = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API}/onboarding`, {
        method: "POST",
        body: fd,   // DO NOT set Content-Type header — browser sets it with boundary automatically
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setApiError(data.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(current)) return;
    setErrors({});
    if (current < STEPS.length) {
      setCurrent((c) => c + 1);
    } else {
      // Final step — submit
      await submitToBackend();
    }
  };

  const handlePrev = () => {
    setErrors({});
    setCurrent((c) => Math.max(1, c - 1));
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">

        <div className="onboarding-header">
          <h1>Employee Onboarding Form</h1>
          <p>Please fill in your details to complete the onboarding process</p>
        </div>

        <Stepper current={current} setCurrent={setCurrent} submitted={submitted} />

        <div className="onboarding-card">
          {submitted ? (
            <div className="success-screen">
              <div className="success-illustration">
                <div className="success-circle-outer">
                  <div className="success-circle-inner">
                    <img src="/assets/icons/all_rounded.png" alt="success" className="success-icon-img" />
                  </div>
                </div>
              </div>
              <h2>Form submitted successfully!</h2>
              <p className="success-green-text">Thank you for completing your onboarding form.</p>
              <p className="success-sub-text">
                Your information has been received and is now being reviewed<br />by our HR team.
              </p>
              <button className="btn-back-home" onClick={() => window.location.href = "https://inypeople.com/"}>
  Back To Home
</button>
            </div>
          ) : (
            <>
              <div className="step-title-row">
                <h2>{STEP_TITLES[current - 1]}</h2>
              </div>

              {current === 1 && <Step1 data={getStep(1)} set={(d) => updateStep(1, d)} errors={errors} />}
              {current === 2 && <Step2 data={getStep(2)} set={(d) => updateStep(2, d)} errors={errors} />}
              {current === 3 && <Step3 data={getStep(3)} set={(d) => updateStep(3, d)} errors={errors} />}
              {current === 4 && <Step4 data={getStep(4)} set={(d) => updateStep(4, d)} errors={errors} />}
              {current === 5 && <Step5 data={getStep(5)} set={(d) => updateStep(5, d)} errors={errors} />}
              {current === 6 && <Step6 data={getStep(6)} set={(d) => updateStep(6, d)} errors={errors} />}

              {apiError && (
                <div style={{ color: "#dc2626", marginTop: "12px", fontSize: "14px", textAlign: "center" }}>
                  {apiError}
                </div>
              )}

              <div className="nav-row">
                <button className="btn-prev" onClick={handlePrev} disabled={current === 1 || submitting}>
                  ← Previous
                </button>
                <button
                  className={`btn-next ${current === STEPS.length ? "btn-submit" : ""}`}
                  onClick={handleNext}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : current === STEPS.length ? "Submit" : "Next →"}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
